import { faNoteSticky } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { Notification } from "@/services/notification-services";
import { formatDistanceToNow } from "@/utils/formatDate";

interface NotificationItemProps {
  notification: Notification;
  onRead: (id: number) => void;
}

export function NotificationItem({ notification, onRead }: NotificationItemProps) {
  const navigate = useNavigate();
  const isUnread = notification.read_at === null;

  const handleClick = () => {
    if (isUnread) {
      onRead(notification.id);
    }
    if (notification.workout_id) {
      navigate(`/workout/${notification.workout_id}`);
    }
  };

  const senderName =
    notification.sender_first_name
      ? `${notification.sender_first_name}${notification.sender_last_name ? ` ${notification.sender_last_name}` : ""}`
      : (notification.sender_username ?? "Your trainer");

  // Replace **bold** markdown markers with actual bold spans for the message
  const renderMessage = (text: string) => {
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, i) =>
      i % 2 === 1 ? (
        <strong key={i} className="font-semibold">
          {part}
        </strong>
      ) : (
        <span key={i}>{part}</span>
      ),
    );
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors hover:bg-light-muted/40 dark:hover:bg-dark-surface/40 ${
        isUnread
          ? "bg-light-primary/5 dark:bg-dark-primary/10 border-l-2 border-light-primary dark:border-dark-primary"
          : "border-l-2 border-transparent"
      }`}
    >
      <div
        className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUnread
            ? "bg-light-primary/15 dark:bg-dark-primary/20"
            : "bg-light-muted/50 dark:bg-dark-surface/50"
        }`}
      >
        <FontAwesomeIcon
          icon={faNoteSticky}
          className={`text-sm ${isUnread ? "text-light-primary dark:text-dark-primary" : "text-light/60 dark:text-dark/60"}`}
        />
      </div>

      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-snug break-words ${isUnread ? "text-light dark:text-dark" : "text-light/70 dark:text-dark/70"}`}>
          {renderMessage(notification.message)}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-light/50 dark:text-dark/50">
            {senderName}
          </span>
          <span className="text-xs text-light/40 dark:text-dark/40">·</span>
          <span className="text-xs text-light/50 dark:text-dark/50">
            {formatDistanceToNow(new Date(notification.created_at))}
          </span>
        </div>
      </div>

      {isUnread && (
        <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-light-primary dark:bg-dark-primary" />
      )}
    </button>
  );
}
